/** Web push on this device (ADR 0037): the browser's own subscription, mirrored on the server under the signed-in person. */
import { del, get, post } from './api.ts'
import { t } from './i18n.ts'

export interface PushState { available: boolean; publicKey: string | null; subscriptions: number }

export const pushSupported = (): boolean => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
/** iOS Safari only delivers push to an app added to the home screen (16.4 or newer). */
export const isStandalone = (): boolean => window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true
export const isIos = (): boolean => /iPhone|iPad|iPod/.test(navigator.userAgent)

export const fetchPushState = () => get<PushState>('/api/me/push')

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null
  const reg = await navigator.serviceWorker.getRegistration('/sw.js')
  return reg ? reg.pushManager.getSubscription() : null
}

function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4)
  const raw = atob((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/** Asks for permission (only on the person's tap), registers the worker, subscribes and tells the server. */
export async function enablePush(publicKey: string): Promise<void> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error(t('Sem permissão para avisos neste navegador. Libere as notificações nas configurações do site.'))
  const reg = await navigator.serviceWorker.register('/sw.js')
  await navigator.serviceWorker.ready
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }))
  const j = sub.toJSON()
  await post('/api/me/push', { endpoint: sub.endpoint, keys: { p256dh: j.keys?.p256dh, auth: j.keys?.auth }, userAgent: navigator.userAgent.slice(0, 300) })
}

/** Removes this device on the server, then in the browser. */
export async function disablePush(): Promise<void> {
  const sub = await currentSubscription()
  if (!sub) return
  await del(`/api/me/push?endpoint=${encodeURIComponent(sub.endpoint)}`)
  await sub.unsubscribe()
}
